"use strict";
/**
 * Row fingerprints: one Bloom filter of row ids per row shard and write
 * session, stored next to the shard entry of the database root manifest.
 * A reader asks every fingerprint of a shard; "maybe" means the shard has to
 * be loaded to confirm the row, "no" is exact.
 *
 * Every writer that brings rows into a shard adds them to its own session's
 * fingerprint and records the clock its client reached in the shard document
 * (`rowWriteClock:<clientId>`). Only the server follow-up of a persisted shard
 * condenses: a writer that leaves more fingerprints or clocks than
 * DATABASE_ROW_FINGERPRINT_CONDENSE_THRESHOLD marks the shard document, and
 * the follow-up replaces them by one fingerprint of a durable snapshot that
 * reaches every clock; `rowFingerprintSeq` names that snapshot.
 *
 * The byte layout and the hash are fixed so every runtime that writes a
 * fingerprint produces identical bytes for the same row ids:
 *
 *   byte 0      format version (1)
 *   byte 1      hash count k (1..32)
 *   bytes 2..5  row capacity, uint32 little endian
 *   bytes 6..9  stored row count, uint32 little endian
 *   bytes 10..  bit array, bit i is (byte 10 + i >> 3) & (1 << (i & 7))
 *
 * Bit positions use enhanced double hashing over MurmurHash3 x86_32 of the
 * UTF-8 row id, all sums wrapping at 2^32: x = murmur(id, 0),
 * y = murmur(id, 0x9747b28c); for i = 0..k-1: position_i = x mod m, then
 * x += y, y += i. Plain (h1 + i * h2) mod m would cycle through few bits
 * whenever h2 mod m shares a factor with m.
 *
 * Readers take k from byte 1 and m from the length, so fingerprints written
 * with another false-positive rate read the same way; a session extends its
 * fingerprint with that fingerprint's own k.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DATABASE_ROW_FINGERPRINT_SESSION = exports.DATABASE_ROW_FINGERPRINT_FALSE_POSITIVE_RATE = exports.DATABASE_ROW_FINGERPRINT_CONDENSE_THRESHOLD = exports.DATABASE_ROW_WRITE_CLOCK_KEY_PREFIX = exports.DATABASE_ROW_FINGERPRINT_KEY_PREFIX = void 0;
exports.databaseRowFingerprintProbe = databaseRowFingerprintProbe;
exports.databaseRowFingerprintCapacity = databaseRowFingerprintCapacity;
exports.databaseRowFingerprintRowCount = databaseRowFingerprintRowCount;
exports.createDatabaseRowFingerprint = createDatabaseRowFingerprint;
exports.growDatabaseRowFingerprint = growDatabaseRowFingerprint;
exports.recordDatabaseShardRowFingerprints = recordDatabaseShardRowFingerprints;
exports.databaseShardAddedRowIds = databaseShardAddedRowIds;
exports.recordDatabaseShardRowWriteClocks = recordDatabaseShardRowWriteClocks;
exports.mergeDatabaseShardRowWrites = mergeDatabaseShardRowWrites;
exports.databaseShardActorWriteClocks = databaseShardActorWriteClocks;
exports.recordDatabaseShardRowWrite = recordDatabaseShardRowWrite;
exports.createDatabaseShardRowWriteFields = createDatabaseShardRowWriteFields;
exports.databaseShardRowWriteCondenseDue = databaseShardRowWriteCondenseDue;
exports.condenseDatabaseShardRowFingerprintsFromSnapshot = condenseDatabaseShardRowFingerprintsFromSnapshot;
exports.condenseDatabaseShardRowFingerprints = condenseDatabaseShardRowFingerprints;
exports.databaseRowFingerprintMayContain = databaseRowFingerprintMayContain;
exports.databaseRowFingerprintsMayContain = databaseRowFingerprintsMayContain;
exports.isDatabaseRowFingerprint = isDatabaseRowFingerprint;
const database_hash_1 = require("./database-hash");
/** Entry keys `rowFingerprint:<session>` hold the fingerprint of one session. */
exports.DATABASE_ROW_FINGERPRINT_KEY_PREFIX = 'rowFingerprint:';
/**
 * Entry keys `rowWriteClock:<clientId>` hold the Yjs clock a client of the
 * shard document reached with a write that brought rows into the shard. A
 * shard document whose state vector reaches every clock holds those rows.
 */
exports.DATABASE_ROW_WRITE_CLOCK_KEY_PREFIX = 'rowWriteClock:';
/**
 * A shard entry with more fingerprints or write clocks than this is due for
 * condensing by the server follow-up of the persisted shard.
 */
exports.DATABASE_ROW_FINGERPRINT_CONDENSE_THRESHOLD = 4;
/** Target false-positive rate of one fingerprint at its capacity. */
exports.DATABASE_ROW_FINGERPRINT_FALSE_POSITIVE_RATE = 0.0001;
const FORMAT_VERSION = 1;
const HEADER_BYTES = 10;
/** Hash counts a fingerprint may carry: rates down to one in 2^32. */
const MAX_HASH_COUNT = 32;
const SECOND_SEED = 0x9747b28c;
/** A session that outgrows its capacity rebuilds with this much headroom. */
const GROWTH_FACTOR = 1.25;
const MIN_CAPACITY = 16;
const encoder = new TextEncoder();
function bitCount(fingerprint) {
    return (fingerprint.length - HEADER_BYTES) * 8;
}
function hashCount(fingerprint) {
    return fingerprint[1];
}
function readUint32(bytes, offset) {
    return ((bytes[offset] |
        (bytes[offset + 1] << 8) |
        (bytes[offset + 2] << 16) |
        (bytes[offset + 3] << 24)) >>>
        0);
}
function writeUint32(bytes, offset, value) {
    bytes[offset] = value & 0xff;
    bytes[offset + 1] = (value >>> 8) & 0xff;
    bytes[offset + 2] = (value >>> 16) & 0xff;
    bytes[offset + 3] = (value >>> 24) & 0xff;
}
/** The two hashes of `rowId` every fingerprint lookup of that row uses. */
function databaseRowFingerprintProbe(rowId) {
    const bytes = encoder.encode(rowId);
    return { x: (0, database_hash_1.murmur3)(bytes, 0), y: (0, database_hash_1.murmur3)(bytes, SECOND_SEED) };
}
function forEachPosition(probe, fingerprint, visit) {
    const bits = bitCount(fingerprint);
    const hashes = hashCount(fingerprint);
    let { x, y } = probe;
    for (let i = 0; i < hashes; i++) {
        if (visit(x % bits) === false)
            return false;
        x = (x + y) >>> 0;
        y = (y + i) >>> 0;
    }
    return true;
}
function setRow(fingerprint, rowId) {
    forEachPosition(databaseRowFingerprintProbe(rowId), fingerprint, position => {
        fingerprint[HEADER_BYTES + (position >>> 3)] |= 1 << (position & 7);
    });
}
/** Row capacity of a fingerprint. */
function databaseRowFingerprintCapacity(fingerprint) {
    return readUint32(fingerprint, 2);
}
/** Number of rows added to a fingerprint (duplicates included). */
function databaseRowFingerprintRowCount(fingerprint) {
    return readUint32(fingerprint, 6);
}
/**
 * A fingerprint of exactly these rows, sized for `capacity` rows at
 * `falsePositiveRate`.
 */
function createDatabaseRowFingerprint(rowIds, capacity = rowIds.length, falsePositiveRate = exports.DATABASE_ROW_FINGERPRINT_FALSE_POSITIVE_RATE) {
    const hashes = Math.round(-Math.log(falsePositiveRate) / Math.LN2);
    if (!(hashes >= 1 && hashes <= MAX_HASH_COUNT))
        throw new RangeError(`Database row fingerprint at a false-positive rate of ${falsePositiveRate} needs ${hashes} hashes; readers take 1 to ${MAX_HASH_COUNT}`);
    const rows = Math.max(MIN_CAPACITY, capacity, rowIds.length);
    const bitsPerRow = -Math.log(falsePositiveRate) / (Math.LN2 * Math.LN2);
    const fingerprint = new Uint8Array(HEADER_BYTES + Math.ceil((rows * bitsPerRow) / 8));
    fingerprint[0] = FORMAT_VERSION;
    fingerprint[1] = hashes;
    writeUint32(fingerprint, 2, rows);
    writeUint32(fingerprint, 6, rowIds.length);
    for (const rowId of rowIds)
        setRow(fingerprint, rowId);
    return fingerprint;
}
/**
 * The next fingerprint of a write session that wrote `current` and now adds
 * `added`; `shardRowIds` are the rows the shard holds after the write. Within
 * capacity the bits are added to a copy, with that fingerprint's own hash
 * count. Beyond it the session rebuilds with headroom from the shard rows its
 * fingerprint holds plus the added ones, at the rate this runtime writes.
 */
function growDatabaseRowFingerprint(current, shardRowIds, added) {
    if (!added.length && current)
        return current;
    const count = (current ? databaseRowFingerprintRowCount(current) : 0) + added.length;
    if (current && count <= databaseRowFingerprintCapacity(current)) {
        const next = current.slice();
        writeUint32(next, 6, count);
        for (const rowId of added)
            setRow(next, rowId);
        return next;
    }
    const adding = new Set(added);
    const rows = [
        ...(current
            ? shardRowIds.filter(rowId => !adding.has(rowId) &&
                databaseRowFingerprintMayContain(current, databaseRowFingerprintProbe(rowId)))
            : []),
        ...adding,
    ];
    return createDatabaseRowFingerprint(rows, current ? Math.ceil(rows.length * GROWTH_FACTOR) : rows.length);
}
/**
 * Write session of this runtime (browser tab, worker, server process). Each
 * session writes only its own fingerprint key, so concurrent sessions never
 * overwrite each other's bits.
 */
exports.DATABASE_ROW_FINGERPRINT_SESSION = [Math.random(), Math.random()]
    .map(value => value.toString(36).slice(2, 8))
    .join('');
/**
 * Fingerprints of a shard after `session` brought `addedRowIds` into it;
 * `shardRowIds` are the rows the shard holds after the write. The added rows
 * always go into the session's own fingerprint, never only into the union:
 * the server follow-up may condense the other keys away at the same time,
 * and a concurrent update of the own key survives that (it deletes only the
 * keys it read). Removed rows stay until condensed.
 *
 * A row the own key reports only through a false positive is never lost: a
 * rebuild of the own key takes every shard row the old key reports.
 */
function recordDatabaseShardRowFingerprints(fingerprints, shardRowIds, addedRowIds, session = exports.DATABASE_ROW_FINGERPRINT_SESSION) {
    const own = fingerprints?.[session];
    const missing = [...new Set(addedRowIds)].filter(rowId => !own ||
        !databaseRowFingerprintMayContain(own, databaseRowFingerprintProbe(rowId)));
    if (!missing.length)
        return fingerprints;
    return {
        ...fingerprints,
        [session]: growDatabaseRowFingerprint(own, shardRowIds, missing),
    };
}
/** Rows of `shardRowIds` the write brought in: absent from `previousRowIds`. */
function databaseShardAddedRowIds(previousRowIds, shardRowIds) {
    const previous = new Set(previousRowIds);
    return shardRowIds.filter(rowId => !previous.has(rowId));
}
/**
 * Write clocks after a write that brought rows in reached `reached` (Yjs
 * client id to clock) in the shard document. Clocks only grow.
 */
function recordDatabaseShardRowWriteClocks(clocks, reached) {
    let next;
    for (const [clientId, clock] of reached) {
        const key = String(clientId);
        if (((next ?? clocks)?.[key] ?? -1) >= clock)
            continue;
        next ??= { ...clocks };
        next[key] = clock;
    }
    return next ?? clocks;
}
/** One write after `first` and `second` of the same atomic mutation. */
function mergeDatabaseShardRowWrites(first, second) {
    const clocks = new Map(first.clocks);
    for (const [clientId, clock] of second.clocks)
        if ((clocks.get(clientId) ?? -1) < clock)
            clocks.set(clientId, clock);
    return {
        addedRowIds: [...new Set([...first.addedRowIds, ...second.addedRowIds])],
        clocks,
    };
}
/** Write clocks of a writer whose shard document client reached `nextClock`. */
function databaseShardActorWriteClocks(actor) {
    return new Map([[actor.clientId, actor.nextClock]]);
}
/**
 * Fingerprints and write clocks of a shard entry after `write`; `shardRowIds`
 * are the rows the shard holds after it. A write that brought no rows in
 * changes neither.
 */
function recordDatabaseShardRowWrite(entry, shardRowIds, write, session = exports.DATABASE_ROW_FINGERPRINT_SESSION) {
    if (!write.addedRowIds.length)
        return {
            rowFingerprints: entry.rowFingerprints,
            rowWriteClocks: entry.rowWriteClocks,
        };
    return {
        rowFingerprints: recordDatabaseShardRowFingerprints(entry.rowFingerprints, shardRowIds, write.addedRowIds, session),
        rowWriteClocks: recordDatabaseShardRowWriteClocks(entry.rowWriteClocks, write.clocks),
    };
}
/**
 * Fingerprint fields of a new shard entry whose writer brought all
 * `shardRowIds` in, reaching `clocks` in its shard document.
 */
function createDatabaseShardRowWriteFields(shardRowIds, clocks, session = exports.DATABASE_ROW_FINGERPRINT_SESSION) {
    return recordDatabaseShardRowWrite({}, shardRowIds, { addedRowIds: shardRowIds, clocks }, session);
}
/**
 * Whether recording `write` (recordDatabaseShardRowWrite) leaves the entry
 * due for condensing. Only a write that brought rows in decides; its writer
 * then marks the shard document (DATABASE_ROW_SHARD_CONDENSE_MAP_KEY).
 */
function databaseShardRowWriteCondenseDue(entry, write, session = exports.DATABASE_ROW_FINGERPRINT_SESSION) {
    if (!write.addedRowIds.length)
        return false;
    const sessions = new Set(Object.keys(entry.rowFingerprints ?? {}));
    sessions.add(session);
    const clients = new Set(Object.keys(entry.rowWriteClocks ?? {}));
    for (const clientId of write.clocks.keys())
        clients.add(String(clientId));
    return (sessions.size > exports.DATABASE_ROW_FINGERPRINT_CONDENSE_THRESHOLD ||
        clients.size > exports.DATABASE_ROW_FINGERPRINT_CONDENSE_THRESHOLD);
}
/**
 * Condensed fingerprint state of a shard from its persisted snapshot, or
 * `null` when the snapshot cannot prove it holds every row the fingerprints
 * stand for: a write clock it does not reach, or an older snapshot than the
 * one the last condensed fingerprint was built from. Only the server
 * follow-up condenses: its snapshot is the newest persisted one, which holds
 * every accepted update up to its sequence.
 */
function condenseDatabaseShardRowFingerprintsFromSnapshot(entry, snapshot, session = exports.DATABASE_ROW_FINGERPRINT_SESSION) {
    if (snapshot.seq < (entry.rowFingerprintSeq ?? 0))
        return null;
    for (const [clientId, clock] of Object.entries(entry.rowWriteClocks ?? {}))
        if ((snapshot.stateVector.get(Number(clientId)) ?? 0) < clock)
            return null;
    return {
        rowFingerprints: condenseDatabaseShardRowFingerprints(snapshot.rowIds, session),
        rowFingerprintSeq: snapshot.seq,
    };
}
/**
 * One fingerprint of exactly `shardRowIds`, replacing every session's entry.
 * Writers that know a shard's whole row set use it when they merge, split,
 * copy or create shards.
 */
function condenseDatabaseShardRowFingerprints(shardRowIds, session = exports.DATABASE_ROW_FINGERPRINT_SESSION) {
    return shardRowIds.length
        ? { [session]: createDatabaseRowFingerprint(shardRowIds) }
        : undefined;
}
/** False only when the row was never added to this fingerprint. */
function databaseRowFingerprintMayContain(fingerprint, probe) {
    return forEachPosition(probe, fingerprint, position => (fingerprint[HEADER_BYTES + (position >>> 3)] & (1 << (position & 7))) !==
        0);
}
/** Union check over all session fingerprints of one shard. */
function databaseRowFingerprintsMayContain(fingerprints, probe) {
    return fingerprints.some(fingerprint => databaseRowFingerprintMayContain(fingerprint, probe));
}
/**
 * Rejects bytes that are not a fingerprint of this format: any hash count
 * from 1 to 32 and a bit array of at least one bit per row of its capacity.
 */
function isDatabaseRowFingerprint(value) {
    if (!(value instanceof Uint8Array) || value.length <= HEADER_BYTES)
        return false;
    const capacity = readUint32(value, 2);
    return (value[0] === FORMAT_VERSION &&
        value[1] >= 1 &&
        value[1] <= MAX_HASH_COUNT &&
        capacity >= MIN_CAPACITY &&
        bitCount(value) >= capacity);
}
